/**
 * PackagingCard — one printed packing card.
 *
 * It is transcribed from `renderPackagingCard` in the retired
 * `backend/src/contexts/delivery/domain/packaging-format.ts`, and #1902 put
 * back the three things the React rewrite lost or invented:
 *
 *  - the card has NO border and NO rounded corners. It used to be a shadcn
 *    `Card`, which brings `rounded-lg border`; the original separates cards
 *    with CUT LINES (see `packagingPrintStyles`), because the operator cuts
 *    the sheet by hand and a printed line is a line to cut along, not a
 *    decoration.
 *  - every colour is a literal. The card renders into a separate window whose
 *    document links none of the app's CSS, so `bg-primary` and
 *    `text-muted-foreground` resolve to nothing there — and, before that
 *    window existed, to the app's indigo and grey. The driver pill is `#111`
 *    on `#fff`; the cut-line grey is `#777`; everything else inherits the
 *    document's `#111`.
 *  - no Tailwind utility classes at all, for the same reason: there is no
 *    Tailwind in that document. Layout is inline, in absolute units.
 *
 * The class names below (`card`, `driver-pill`, `dishes`, …) are the original
 * stylesheet's, and only the rules that CANNOT be inline live there: the cut
 * lines are a background plus an `:nth-last-child` exception.
 *
 * The shrink factor still comes from the backend:
 *   backend/src/contexts/delivery/domain/packaging-card.ts (computeCardScale)
 * consumed as `entry.scale`. It is content-driven and deterministic, so it
 * stays server-side; the BOX geometry below is the only copy.
 */

import { Fragment, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { PackagingCustomerEntry, PackagingItem } from './packagingData';

/** Card box geometry — mirrors the `.card` rule in packagingToHtml. */
export const PACKAGING_CARD_WIDTH = '101mm';
export const PACKAGING_CARD_HEIGHT = '50mm';
/** Printed sheet is 207mm wide = 2 x 101mm + one 5mm column gap. */
export const PACKAGING_CARD_COLUMN_GAP = '5mm';

const CARD_PADDING = '2.6mm 3mm';
/** Vertical rhythm between blocks; matches `computeCardScale`'s BLOCK_GAP. */
const BLOCK_GAP = '1mm';

/** The cut-line grey — outlines that are guides, not content. */
const RULE_GREY = '#777';
/** The document's ink. Every text colour on the card is this or `#fff`. */
export const PACKAGING_INK = '#111';
export const PACKAGING_PAPER = '#fff';

type TypeKey = 'job' | 'bag' | 'driver' | 'addr' | 'combo' | 'dish' | 'gift' | 'note';

const TYPE_SCALE: Record<TypeKey, { pt: number; lineHeight: number }> = {
  job: { pt: 14, lineHeight: 1.08 },
  bag: { pt: 12, lineHeight: 1 },
  driver: { pt: 8.5, lineHeight: 1 },
  addr: { pt: 10, lineHeight: 1.22 },
  combo: { pt: 9, lineHeight: 1.2 },
  dish: { pt: 8, lineHeight: 1.3 },
  gift: { pt: 7.5, lineHeight: 1.25 },
  note: { pt: 9.5, lineHeight: 1.24 },
};

/**
 * Every zone's font-size is `calc(<base>pt * var(--fs))` so the card obeys
 * the backend-computed shrink factor exactly like the print renderer does.
 */
function typeStyle(key: TypeKey): CSSProperties {
  const { pt, lineHeight } = TYPE_SCALE[key];
  return { fontSize: `calc(${pt}pt * var(--fs))`, lineHeight };
}

/** `style` carrying the scale factor the zone font-sizes reference. */
interface ScaledCardStyle extends CSSProperties {
  '--fs': string;
}

interface PackagingCardProps {
  entry: PackagingCustomerEntry;
}

interface PackagingItemRowProps {
  item: PackagingItem;
  giftLabel: string;
  unallocatedLabel: string;
}

const ITEM_LINE_STYLE: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: BLOCK_GAP,
};

/**
 * An outlined label: the badge on a combo's gift line (zone H) and the
 * unplaced mark on an item line (#2647). The gift badge reads the GIFT clause
 * parsed out of the combo TITLE — see `combo.gift` below — and has nothing to
 * do with the retired #930 add-a-gift mechanism (#1905).
 */
function OutlineBadge({ label, className }: { label: string; className: string }) {
  return (
    <span
      className={className}
      style={{
        display: 'inline-block',
        flex: '0 0 auto',
        padding: '0 0.5mm',
        border: '0.2mm solid currentColor',
        lineHeight: 1.1,
        fontWeight: 700,
      }}
    >
      {label}
    </span>
  );
}

/** Right-aligned `× N` — every item line carries it, combo or raw (#1679). */
function QuantityLabel({ quantity }: { quantity: number }) {
  return (
    <span className="item-quantity" style={{ flex: '0 0 auto', marginLeft: 'auto' }}>
      × {quantity}
    </span>
  );
}

function PackagingItemRow({ item, giftLabel, unallocatedLabel }: PackagingItemRowProps) {
  // `combo === null` — the title did not parse as a combo, so the whole
  // raw title goes on one line (same fallback as renderPackagingItem).
  if (item.combo === null) {
    return (
      <div
        className="item-line raw-item"
        style={{ ...ITEM_LINE_STYLE, ...typeStyle('combo') }}
        data-zone="F"
      >
        <span className="raw-title" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
          {item.title}
        </span>{' '}
        {item.unallocated && (
          <OutlineBadge label={unallocatedLabel} className="unallocated-badge" />
        )}{' '}
        <QuantityLabel quantity={item.quantity} />
      </div>
    );
  }

  return (
    <Fragment>
      <div
        className="item-line combo-line"
        style={{ ...ITEM_LINE_STYLE, ...typeStyle('combo'), fontWeight: 600 }}
        data-zone="F"
      >
        <span className="combo-name" style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
          {item.combo.name}
        </span>{' '}
        {item.product_type !== null && (
          <span
            className="item-bag-type"
            style={{
              flex: '0 0 auto',
              padding: '0 0.7mm',
              // Grey outline, black text: the box is a guide, the type is
              // content. Tying them together (`currentColor` under a muted
              // class) is what turned the label grey (#1902).
              border: `0.2mm solid ${RULE_GREY}`,
              borderRadius: '0.5mm',
            }}
          >
            {item.product_type}
          </span>
        )}{' '}
        {item.unallocated && (
          <OutlineBadge label={unallocatedLabel} className="unallocated-badge" />
        )}{' '}
        <QuantityLabel quantity={item.quantity} />
      </div>
      {item.combo.dishes.length > 0 && (
        <div
          className="dishes"
          style={{ ...typeStyle('dish'), overflowWrap: 'anywhere' }}
          data-zone="G"
        >
          {item.combo.dishes.join(' · ')}
        </div>
      )}
      {item.combo.gift !== null && (
        <div
          className="gift-line"
          style={{ ...ITEM_LINE_STYLE, ...typeStyle('gift'), gap: '0.8mm' }}
          data-zone="H"
        >
          <OutlineBadge label={giftLabel} className="gift-badge" /> <span>{item.combo.gift}</span>
        </div>
      )}
    </Fragment>
  );
}

export default function PackagingCard({ entry }: PackagingCardProps) {
  const { t } = useTranslation();
  const giftLabel = t('packaging.gift.badge');
  const unallocatedLabel = t('packaging.item.unallocated');

  // One joined summary string, exactly like the print card's `.bag-summary`
  // — an empty list renders no element at all.
  const bagLine = entry.bag_types.map(({ type, quantity }) => `${type} × ${quantity}`).join(' · ');

  const notes: Array<{ key: string; label: string; text: string }> = [];
  if (entry.delivery_notes !== null && entry.delivery_notes !== '') {
    notes.push({
      key: 'order',
      label: t('packaging.card.orderNote'),
      text: entry.delivery_notes,
    });
  }
  if (entry.operator_note !== null && entry.operator_note !== '') {
    notes.push({
      key: 'job',
      label: t('packaging.card.jobNote'),
      text: entry.operator_note,
    });
  }

  const cardStyle: ScaledCardStyle = {
    '--fs': String(entry.scale),
    width: PACKAGING_CARD_WIDTH,
    height: PACKAGING_CARD_HEIGHT,
    padding: CARD_PADDING,
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    gap: BLOCK_GAP,
    overflow: 'hidden',
  };

  return (
    // Plain `<article>`, not a shadcn `Card`: the component library's default
    // border is the very thing #1902 is about. `break-inside: avoid` and the
    // cut lines ride the `card` class — see `packagingPrintStyles`.
    <article className="card" data-testid="packaging-card" style={cardStyle}>
      <div
        className="card-head"
        style={{
          display: 'flex',
          alignItems: 'center',
          whiteSpace: 'nowrap',
          gap: '2mm',
          minWidth: 0,
        }}
      >
        <h3
          className="job-name"
          style={{ ...typeStyle('job'), flex: '0 1 auto', minWidth: 0, fontWeight: 700 }}
          data-zone="A"
        >
          {entry.job_name}
        </h3>
        {bagLine !== '' && (
          <div
            className="bag-summary"
            style={{
              ...typeStyle('bag'),
              flex: '0 0 auto',
              padding: '0.2mm 1.45mm',
              border: `0.3mm solid ${PACKAGING_INK}`,
              borderRadius: '0.6mm',
              fontWeight: 700,
            }}
            data-zone="B"
          >
            {bagLine}
          </div>
        )}
        {entry.driver_name !== null && (
          <div
            className="driver-pill"
            style={{
              ...typeStyle('driver'),
              flex: '0 0 auto',
              marginLeft: 'auto',
              padding: '0.4mm 1.6mm',
              borderRadius: '999px',
              backgroundColor: PACKAGING_INK,
              color: PACKAGING_PAPER,
              fontWeight: 700,
            }}
            data-zone="C"
          >
            {entry.driver_name}
          </div>
        )}
      </div>

      {/* No recipient name / phone: the driver reads the address, and zone D
          is retired rather than re-lettered so older screenshots still read
          straight (#1679). */}
      <div
        className="address"
        style={{ ...typeStyle('addr'), overflowWrap: 'anywhere' }}
        data-zone="E"
      >
        {entry.address}
      </div>

      <div
        aria-hidden="true"
        className="separator"
        style={{
          flex: '0 0 0.5mm',
          height: '0.5mm',
          borderTop: `0.2mm solid ${RULE_GREY}`,
        }}
      />

      {entry.items.length > 0 && (
        <div
          className="items-block"
          style={{ display: 'flex', flexDirection: 'column', gap: BLOCK_GAP }}
        >
          {entry.items.map((item, index) => (
            <PackagingItemRow
              key={`${item.line_item_id}:${index}`}
              item={item}
              giftLabel={giftLabel}
              unallocatedLabel={unallocatedLabel}
            />
          ))}
        </div>
      )}

      {notes.length > 0 && (
        <div
          className="notes-block"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: BLOCK_GAP,
            marginTop: 'auto',
          }}
          data-zone="I"
        >
          {notes.map((note) => (
            <div
              key={note.key}
              className="note-line"
              style={{ ...typeStyle('note'), overflowWrap: 'anywhere' }}
            >
              <span className="note-label" style={{ marginRight: '0.8mm', fontWeight: 700 }}>
                {note.label}
              </span>{' '}
              {note.text}
            </div>
          ))}
        </div>
      )}
    </article>
  );
}
